import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSocket } from '../utils/socket';

// Component mô phỏng màn hình hạt mè nhiễu TV Static + "NO SIGNAL" chuẩn OSD
function NoSignalScreen({ status, isPortrait }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let timerId;
    const cols = 52;
    const rows = 38;

    const drawNoise = () => {
      const w = canvas.width;
      const h = canvas.height;
      const cellW = w / cols;
      const cellH = h / rows;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const val = Math.floor(Math.random() * 256);
          ctx.fillStyle = `rgb(${val},${val},${val})`;
          ctx.fillRect(c * cellW, r * cellH, cellW + 0.5, cellH + 0.5);
        }
      }
      timerId = setTimeout(() => {
        requestAnimationFrame(drawNoise);
      }, 70);
    };

    drawNoise();
    return () => clearTimeout(timerId);
  }, []);

  return (
    <div style={{ ...s.noSignalWrap, aspectRatio: isPortrait ? '3/4' : '4/3' }}>
      <canvas ref={canvasRef} width={320} height={240} style={s.noSignalCanvas} />
      <div style={s.noSignalOsd}>
        <div style={s.noSignalIcon}>📹❌</div>
        <div style={s.noSignalTitle}>NO SIGNAL</div>
        <div style={s.noSignalSub}>{status || 'Chưa có tín hiệu camera'}</div>
      </div>
    </div>
  );
}

export default function Viewer() {
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const imgRef = useRef(null);
  const lastBlobUrlRef = useRef(null);

  // Quản lý âm thanh Web Audio API
  const audioCtxRef = useRef(null);
  const nextStartTimeRef = useRef(0);
  const isMicOnRef = useRef(false);

  const [hasFrame, setHasFrame] = useState(false);
  const [hasCameraSignal, setHasCameraSignal] = useState(false);
  const [status, setStatus] = useState('Đang kết nối...');
  const [orientation, setOrientation] = useState('landscape');
  const [colorMode, setColorMode] = useState('normal');
  const [flashOn, setFlashOn] = useState(false);
  const [flashSupported, setFlashSupported] = useState(true);
  const [controlWarning, setControlWarning] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState(null);

  // Mic nghe âm thanh & Bảng chỉnh màu 1-99
  const [isMicOn, setIsMicOn] = useState(false);
  const [showAdjustPanel, setShowAdjustPanel] = useState(false);
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(1.0);

  const username = localStorage.getItem('username');
  const token = localStorage.getItem('token');
  const isPortrait = orientation === 'portrait';

  // Chuyển đổi giá trị sang dải số 1 đến 99 (ở giữa chuẩn là 50)
  const brightnessLevel = Math.round(1 + ((brightness + 80) / 160) * 98);
  const contrastLevel = Math.round(1 + ((contrast - 0.2) / 1.6) * 98);

  useEffect(() => {
    isMicOnRef.current = isMicOn;
  }, [isMicOn]);

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.padding = '0';

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    };
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const socket = createSocket(token, 'web-viewer');
    socketRef.current = socket;

    socket.on('connect', () => {
      setStatus('Đã kết nối máy chủ — chờ Máy Phát...');
      socket.emit('request-offer');
    });

    socket.on('connect_error', () => {
      setStatus('Không kết nối được máy chủ — đang thử lại...');
    });

    socket.on('disconnect', () => {
      setStatus('Mất kết nối máy chủ — đang kết nối lại...');
      if (lastBlobUrlRef.current) {
        URL.revokeObjectURL(lastBlobUrlRef.current);
        lastBlobUrlRef.current = null;
      }
      setHasFrame(false);
      setHasCameraSignal(false);
    });

    socket.on('broadcaster-offline', () => {
      setStatus('Máy Phát hiện chưa hoạt động (NO SIGNAL)');
      if (lastBlobUrlRef.current) {
        URL.revokeObjectURL(lastBlobUrlRef.current);
        lastBlobUrlRef.current = null;
      }
      setHasFrame(false);
      setHasCameraSignal(false);
      setBatteryLevel(null);
    });

    socket.on('broadcaster-online', () => {
      setStatus('Máy Phát đã online, chờ video...');
    });

    socket.on('camera-status', ({ connected }) => {
      setHasCameraSignal(!!connected);
      if (!connected) {
        setHasFrame(false);
        if (lastBlobUrlRef.current) {
          URL.revokeObjectURL(lastBlobUrlRef.current);
          lastBlobUrlRef.current = null;
        }
        setStatus('Máy Phát chưa kết nối camera (NO SIGNAL)');
      }
    });

    socket.on('fpv-frame', (data) => {
      if (!data) return;
      try {
        const blob = new Blob([data], { type: 'image/jpeg' });
        const newUrl = URL.createObjectURL(blob);
        if (imgRef.current) {
          const prevUrl = lastBlobUrlRef.current;
          imgRef.current.onload = () => {
            if (prevUrl) URL.revokeObjectURL(prevUrl);
          };
          imgRef.current.src = newUrl;
          lastBlobUrlRef.current = newUrl;
        }
        setHasFrame(true);
        setHasCameraSignal(true);
        setStatus((prev) => (prev !== '🔴 Đang xem trực tiếp' ? '🔴 Đang xem trực tiếp' : prev));
      } catch (err) {
        console.error('Lỗi đọc khung hình:', err);
      }
    });

    socket.on('fpv-audio', (data) => {
      if (!isMicOnRef.current || !data) return;
      const ctx = audioCtxRef.current;
      if (!ctx || ctx.state !== 'running') return;

      try {
        let uint8;
        if (data instanceof Uint8Array) {
          uint8 = data;
        } else if (data instanceof ArrayBuffer) {
          uint8 = new Uint8Array(data);
        } else if (Array.isArray(data)) {
          uint8 = new Uint8Array(data);
        } else if (data.buffer instanceof ArrayBuffer) {
          uint8 = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
        } else {
          return;
        }

        const int16 = new Int16Array(uint8.buffer, uint8.byteOffset, Math.floor(uint8.byteLength / 2));
        const float32 = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) {
          float32[i] = int16[i] / 32768.0;
        }

        const audioBuffer = ctx.createBuffer(1, float32.length, 16000);
        audioBuffer.getChannelData(0).set(float32);

        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);

        const now = ctx.currentTime;
        if (nextStartTimeRef.current < now || nextStartTimeRef.current > now + 0.15) {
          nextStartTimeRef.current = now;
        }
        source.start(nextStartTimeRef.current);
        nextStartTimeRef.current += audioBuffer.duration;
      } catch (err) {
        console.error('Lỗi phát âm thanh:', err);
      }
    });

    socket.on('rotate-command', ({ orientation: o }) => setOrientation(o));
    socket.on('color-mode-command', ({ mode }) => setColorMode(mode));

    socket.on('display-adjust-status', (data) => {
      if (data?.brightness != null) setBrightness(Number(data.brightness));
      if (data?.contrast != null) setContrast(Number(data.contrast));
    });

    socket.on('display-adjust-command', (data) => {
      if (data?.brightness != null) setBrightness(Number(data.brightness));
      if (data?.contrast != null) setContrast(Number(data.contrast));
    });

    socket.on('flash-status', ({ on, supported }) => {
      setFlashOn(!!on);
      if (supported === false) setFlashSupported(false);
    });

    socket.on('control-denied', () => {
      setControlWarning('Có người khác vừa điều khiển, vui lòng thử lại');
      setTimeout(() => setControlWarning(''), 1500);
    });

    socket.on('battery-status', ({ level }) => {
      if (typeof level === 'number') setBatteryLevel(level);
    });

    socket.on('vibrate-command', ({ code }) => {
      const times = { D1: 1, D2: 2, D3: 3, D4: 4 }[code] || 1;
      if (navigator.vibrate) {
        const pattern = [];
        for (let i = 0; i < times; i++) {
          pattern.push(320, 300);
        }
        navigator.vibrate(pattern);
      }
    });

    return () => {
      if (lastBlobUrlRef.current) {
        URL.revokeObjectURL(lastBlobUrlRef.current);
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      socket.disconnect();
    };
  }, [token, navigate]);

  const sendVibrate = (code) => {
    socketRef.current?.emit('vibrate-command', { code });
  };

  const toggleMic = async () => {
    const next = !isMicOn;
    setIsMicOn(next);
    socketRef.current?.emit('audio-listen-toggle', { enabled: next });

    if (next) {
      try {
        if (!audioCtxRef.current) {
          const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
          audioCtxRef.current = new AudioCtxClass({ sampleRate: 16000 });
        }
        if (audioCtxRef.current.state === 'suspended') {
          await audioCtxRef.current.resume();
        }
        nextStartTimeRef.current = audioCtxRef.current.currentTime;
      } catch (err) {
        console.error('Không thể khởi tạo Web Audio:', err);
      }
    } else {
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    }
  };

  const updateDisplayAdjust = (newB, newC) => {
    setBrightness(newB);
    setContrast(newC);
    socketRef.current?.emit('display-adjust-command', {
      brightness: newB,
      contrast: newC,
    });
  };

  const onBrightnessLevelChange = (e) => {
    const level = Number(e.target.value);
    const t = (level - 1) / 98;
    const newB = -80 + t * 160;
    updateDisplayAdjust(newB, contrast);
  };

  const onContrastLevelChange = (e) => {
    const level = Number(e.target.value);
    const t = (level - 1) / 98;
    const newC = 0.2 + t * 1.6;
    updateDisplayAdjust(brightness, newC);
  };

  const toggleOrientation = () => {
    const next = orientation === 'landscape' ? 'portrait' : 'landscape';
    setOrientation(next);
    socketRef.current?.emit('rotate-command', { orientation: next });
  };

  const toggleColorMode = () => {
    const next = colorMode === 'normal' ? 'grayscale' : 'normal';
    setColorMode(next);
    socketRef.current?.emit('color-mode-command', { mode: next });
  };

  const toggleFlash = () => {
    if (!flashSupported) return;
    socketRef.current?.emit('flash-command', { on: !flashOn });
  };

  const handleLogout = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    if (lastBlobUrlRef.current) {
      URL.revokeObjectURL(lastBlobUrlRef.current);
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    socketRef.current?.disconnect();
    localStorage.clear();
    navigate('/login');
  };

  const hasActiveVideo = hasCameraSignal && hasFrame;

  return (
    <div style={s.page}>
      {/* Vùng hiển thị Video hoặc Màn hình nhiễu NO SIGNAL */}
      <div style={s.screenArea}>
        {hasActiveVideo ? (
          <div
            style={{
              ...s.rotator,
              width: isPortrait ? '100dvh' : '100dvw',
              height: isPortrait ? '100dvw' : '100dvh',
              transform: `translate(-50%, -50%) rotate(${isPortrait ? 90 : 0}deg)`,
            }}
          >
            <img
              ref={imgRef}
              alt="Live Stream"
              style={{
                ...s.image,
                filter: colorMode === 'grayscale' ? 'grayscale(100%)' : 'none',
              }}
            />
          </div>
        ) : (
          <NoSignalScreen status={status} isPortrait={isPortrait} />
        )}
      </div>

      {/* Thanh thông tin phía trên */}
      <div style={s.topBar}>
        <div style={s.badge}>📺 {username}</div>
        {batteryLevel !== null && <div style={s.badge}>🔋 {batteryLevel}%</div>}
        <div style={s.statusBadge}>{status}</div>
        <button style={s.logoutBtn} onClick={handleLogout} disabled={loggingOut}>
          {loggingOut ? '...' : 'Đăng xuất'}
        </button>
      </div>

      {controlWarning && <div style={s.warningBanner}>{controlWarning}</div>}

      {/* Bảng trượt chỉnh Sáng/Tối & Đậm/Nhạt */}
      {showAdjustPanel && (
        <div style={s.adjustCard}>
          <div style={s.adjustRow}>
            <span style={s.adjustIcon}>☀️</span>
            <input
              type="range"
              min="1"
              max="99"
              step="1"
              value={brightnessLevel}
              onChange={onBrightnessLevelChange}
              style={s.rangeInput}
            />
            <span style={s.adjustVal}>{brightnessLevel}</span>
          </div>
          <div style={s.adjustRow}>
            <span style={s.adjustIcon}>🌗</span>
            <input
              type="range"
              min="1"
              max="99"
              step="1"
              value={contrastLevel}
              onChange={onContrastLevelChange}
              style={s.rangeInput}
            />
            <span style={s.adjustVal}>{contrastLevel}</span>
          </div>
        </div>
      )}

      {/* Thanh công cụ phía dưới — Co giãn chuẩn trên mobile */}
      <div style={s.bottomBar}>
        <div style={s.vibrateGrid}>
          {['D1', 'D2', 'D3', 'D4'].map((code) => (
            <button key={code} style={s.vibrateBtn} onClick={() => sendVibrate(code)}>
              {code}
            </button>
          ))}
        </div>

        <div style={s.controlsGrid}>
          <button
            style={{
              ...s.actionBtn,
              backgroundColor: isMicOn ? '#15803d' : 'rgba(51, 65, 85, 0.9)',
            }}
            onClick={toggleMic}
          >
            {isMicOn ? '🎙️ Tắt Mic' : '🔇 Bật Mic'}
          </button>
          <button
            style={{
              ...s.actionBtn,
              backgroundColor: showAdjustPanel ? '#0284c7' : 'rgba(51, 65, 85, 0.9)',
            }}
            onClick={() => setShowAdjustPanel(!showAdjustPanel)}
          >
            ⚙️ Chỉnh màu
          </button>
          <button style={s.actionBtn} onClick={toggleFlash}>
            🔦 {flashOn ? 'Tắt đèn' : 'Bật đèn'}
          </button>
          <button style={s.actionBtn} onClick={toggleOrientation}>
            🔄 Xoay ({isPortrait ? 'Dọc' : 'Ngang'})
          </button>
          <button style={s.actionBtn} onClick={toggleColorMode}>
            🎨 {colorMode === 'normal' ? 'Màu chuẩn' : 'Đen trắng'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    width: '100vw',
    height: '100dvh',
    minHeight: '-webkit-fill-available',
    backgroundColor: '#000000',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    userSelect: 'none',
    boxSizing: 'border-box',
  },
  screenArea: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    width: '100%', height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    zIndex: 1,
  },
  rotator: {
    position: 'absolute',
    top: '50%', left: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    pointerEvents: 'none',
    imageRendering: 'high-quality',
    WebkitBackfaceVisibility: 'hidden',
    transform: 'translateZ(0)',
  },
  noSignalWrap: {
    position: 'relative',
    width: '100%',
    maxWidth: '640px',
    maxHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  noSignalCanvas: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  noSignalOsd: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    border: '2px solid #ef4444',
    borderRadius: '12px',
    padding: '14px 24px',
    textAlign: 'center',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.8)',
    pointerEvents: 'none',
  },
  noSignalIcon: {
    fontSize: '28px',
    marginBottom: '4px',
  },
  noSignalTitle: {
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: 900,
    letterSpacing: '3px',
    fontFamily: 'monospace',
  },
  noSignalSub: {
    color: '#fde047',
    fontSize: '12px',
    marginTop: '6px',
  },
  topBar: {
    position: 'absolute',
    top: 'calc(8px + env(safe-area-inset-top, 0px))',
    left: 8, right: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    zIndex: 10,
    pointerEvents: 'none',
    boxSizing: 'border-box',
    maxWidth: 'calc(100% - 16px)',
  },
  badge: {
    pointerEvents: 'auto',
    backgroundColor: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(8px)',
    color: '#f8fafc',
    padding: '5px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    border: '1px solid rgba(255, 255, 255, 0.15)',
    whiteSpace: 'nowrap',
  },
  statusBadge: {
    pointerEvents: 'auto',
    backgroundColor: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(8px)',
    color: '#f8fafc',
    padding: '5px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    border: '1px solid rgba(255, 255, 255, 0.15)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    minWidth: 0,
  },
  logoutBtn: {
    pointerEvents: 'auto',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: 20,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
    boxShadow: '0 2px 6px rgba(239, 68, 68, 0.4)',
  },
  warningBanner: {
    position: 'absolute',
    top: 50, left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(245, 158, 11, 0.95)',
    color: '#0f172a',
    padding: '5px 14px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    zIndex: 15,
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
    whiteSpace: 'nowrap',
  },
  adjustCard: {
    position: 'absolute',
    bottom: 'calc(185px + env(safe-area-inset-bottom, 0px))',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 24px)',
    maxWidth: '480px',
    backgroundColor: 'rgba(91, 155, 213, 0.96)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '12px 14px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)',
    zIndex: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxSizing: 'border-box',
  },
  adjustRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  adjustIcon: {
    fontSize: '20px',
    width: '24px',
    textAlign: 'center',
    color: '#fde047',
  },
  rangeInput: {
    flex: 1,
    accentColor: '#f59e0b',
    height: '6px',
    cursor: 'pointer',
  },
  adjustVal: {
    width: '28px',
    textAlign: 'right',
    color: '#fde047',
    fontSize: '14px',
    fontWeight: 800,
    fontFamily: 'monospace',
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0, left: 0, right: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.9)',
    backdropFilter: 'blur(12px)',
    paddingTop: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    zIndex: 10,
    borderTop: '1px solid rgba(255, 255, 255, 0.12)',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100vw',
    overflow: 'hidden',
  },
  vibrateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 6,
    width: '100%',
    maxWidth: 480,
    boxSizing: 'border-box',
  },
  vibrateBtn: {
    height: 40,
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#f59e0b',
    color: '#0f172a',
    fontWeight: 800,
    fontSize: 14,
    cursor: 'pointer',
    boxShadow: '0 2px 5px rgba(245, 158, 11, 0.3)',
    touchAction: 'manipulation',
  },
  controlsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 5,
    width: '100%',
    maxWidth: 480,
    boxSizing: 'border-box',
  },
  actionBtn: {
    flex: '1 1 auto',
    minWidth: '58px',
    height: 36,
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.15)',
    backgroundColor: 'rgba(51, 65, 85, 0.9)',
    color: '#f8fafc',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    touchAction: 'manipulation',
    padding: '0 6px',
    boxSizing: 'border-box',
  },
};